/**
 * Scene cleanup utilities
 * Handles cleanup when scenes are destroyed
 */

import { scrollManager } from '../../../utils/scroll'
import { gsap } from 'gsap'

export interface SceneCleanupContext {
  fadeInAnimation: gsap.core.Tween | null
  spacingResizeHandler: (() => void) | null
  scrollSoundCleanups: Array<() => void>
  homeworkEscapeHandler: ((e: KeyboardEvent) => void) | null
  homeworkButton: HTMLElement | null
  homeworkOverlay: HTMLElement | null
  rainSceneTriggers?: any[]
}

/**
 * Cleanup scene resources
 */
export function cleanupScene(context: SceneCleanupContext): void {
  scrollManager.removeMaxScroll()

  if (context.spacingResizeHandler) {
    context.spacingResizeHandler()
  }

  for (const cleanup of context.scrollSoundCleanups) {
    cleanup()
  }
  
  // Clean up homework overlay if it exists
  if (context.homeworkEscapeHandler) {
    document.removeEventListener('keydown', context.homeworkEscapeHandler)
  }
  if (context.homeworkButton) {
    context.homeworkButton.remove()
  }
  if (context.homeworkOverlay) {
    context.homeworkOverlay.remove()
  }
  
  // Remove any transform on game-screen so fixed positioning (e.g. story-nav) works
  const gameScreen = document.querySelector('.game-screen') as HTMLElement
  if (gameScreen) {
    gsap.set(gameScreen, { clearProps: 'transform' })
  }
  
  // Restore body overflow if overlay was open
  document.body.style.overflow = ''

  // Clean up fade-in animation ScrollTrigger
  if (context.fadeInAnimation) {
    const st = (context.fadeInAnimation as any).scrollTrigger
    if (st) {
      st.kill()
    }
    context.fadeInAnimation.kill()
  }

  // Clean up rain scene ScrollTriggers
  if (context.rainSceneTriggers) {
    context.rainSceneTriggers.forEach((trigger: any) => {
      if (trigger && trigger.kill) {
        trigger.kill()
      }
    })
  }

  // Clean up scroll-animated scene sticky/animation (same as rain cleanup)
  // The cleanup function is stored on the wrapper, so we need to find it
  // This is handled the same way as rain scenes - the wrapper has the cleanup function
}
